import { createPortal } from 'react-dom';
import React, { useState, useRef, useEffect } from 'react';
import styled, { withTheme } from 'styled-components';

// Styled components
const DropdownWrapper = styled.div`
    position: relative;
    display: inline-block;
`;

const Trigger = styled.div`
    cursor: pointer;
`;

const DropdownContent = styled.div`
    position: absolute;
    background: ${props => props.theme.colors.body ? props.theme.colors.body : 'white'} !important;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    z-index: 9999;
    min-width: 160px;  /* Optional minimum width */
    width: auto;  /* Allow width to grow based on content */
    ${(props) => props.placement === 'bottomLeft' && `
        top: 100%;
        left: 0;
    `}
    ${(props) => props.placement === 'bottomRight' && `
        top: 100%;
        right: 0;
    `}
`;

const StyledMenu = styled.div`
    position: relative;
    z-index: 20001;
    display: flex;
    flex-direction: column;
    padding: 24px;
    border-radius: 8px;
`;

// Custom Dropdown Component
const StyledDropdown = ({ children, overlay, placement = 'bottomLeft', trigger = 'hover', visible, onVisibleChange, theme }) => {
    const [dropdownVisible, setDropdownVisible] = useState(visible);
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);

    // Synchronize the internal state with the visible prop
    useEffect(() => {
        setDropdownVisible(visible);
    }, [visible]);

    // Handle closing the dropdown when clicking outside
    useEffect(() => {
        if (!dropdownVisible) return; // Only add the listener if the dropdown is visible

        const handleClickOutside = (event) => {
            if (dropdownRef.current) {
                const isClickInsideDropdown = dropdownRef.current.contains(event.target);
                const isClickInsideTrigger = triggerRef.current && triggerRef.current.contains(event.target);

                // Handle clicks on the trigger to toggle visibility
                if (isClickInsideTrigger) {
                    return;
                }

                // Handle clicks outside of the dropdown
                if (!isClickInsideDropdown) {
                    // Check if the click is inside any date picker dropdowns
                    const datePickers = document.querySelectorAll('.ant-picker-dropdown');
                    const clickedInsideDatePicker = Array.from(datePickers).some((picker) =>
                        picker.contains(event.target)
                    );

                    if (!clickedInsideDatePicker) {
                        setDropdownVisible(false);
                        onVisibleChange && onVisibleChange(false);
                    }
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownVisible, dropdownRef, triggerRef, onVisibleChange]);


    // Toggle dropdown visibility for click trigger
    const handleToggleDropdown = (event) => {
        event.stopPropagation();
        if (trigger === 'click') {
            const newVisible = !dropdownVisible;
            setDropdownVisible(newVisible);
            if (onVisibleChange) onVisibleChange(newVisible);  // Notify parent component of visibility change
        }
    };

    // Handle mouse events for hover trigger
    const handleMouseEnter = () => {
        if (trigger === 'hover' || !trigger) {
            setDropdownVisible(true);
            if (onVisibleChange) onVisibleChange(true);  // Notify parent component
        }
    };

    const handleMouseLeave = () => {
        if (trigger === 'hover' || !trigger) {
            setTimeout(() => {
                setDropdownVisible(false);
                if (onVisibleChange) onVisibleChange(false);  // Notify parent component
            }, 200);  // Add delay to smooth hover transition
        }
    };

    const dropdownContent = (
        <DropdownContent ref={dropdownRef} placement={placement} className='custom-dropdown-content' theme={theme}>
            {overlay}
        </DropdownContent>
    );

    return (
        <>
            <DropdownWrapper
                ref={dropdownRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Trigger ref={triggerRef} onClick={trigger === 'click' ? handleToggleDropdown : undefined}>{children}</Trigger>
                {dropdownVisible && dropdownContent}
            </DropdownWrapper>
            {/* {dropdownVisible && createPortal(dropdownContent, dropdownRoot)} */}
        </>
    );
};

export const Dropdown = withTheme(StyledDropdown);
export const Menu = StyledMenu;
